import { Divider } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router';

import { MoreHorizIcon } from '@/shared/assets/icons/more-horiz-icon';
import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import { AddResourcesToGroup } from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group';
import { UNGROUPED_RESOURCES_GROUP_ID } from '@/shared/features/groups/components/group-list/group-list.constants';
import { TGroupedItemContextMenuProps } from '@/shared/features/groups/components/grouped-item-context-menu/grouped-item-context-menu.types';
import { MoveResources } from '@/shared/features/groups/components/move-resources/move-resources';
import { RemoveResourcesFromGroup } from '@/shared/features/groups/components/remove-resources-from-group/remove-resources-from-group';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { TPermissionCheck } from '@/shared/features/permissions/permission-check.types';

export const GroupedItemContextMenu: React.FC<TGroupedItemContextMenuProps> = ({
  item,
  additionalOptions,
  additionalDangerOptions,
}) => {
  const { groupId } = useParams();
  const { getItemId } = useDataTable();
  const { permissionActions, useResourceElements } = useGroupsConfig();
  const { getUpdatePermissionAction } = useResourceElements();
  const { can } = usePermissionCheck();
  const updateAction = getUpdatePermissionAction(item.__typename);

  const scopedGroupId =
    groupId === UNGROUPED_RESOURCES_GROUP_ID ? undefined : groupId;
  const hasActions =
    can(permissionActions.updateGroup, scopedGroupId) ||
    Boolean(additionalOptions) ||
    Boolean(additionalDangerOptions);

  const getPermission = (): TPermissionCheck | undefined => {
    if (!updateAction) {
      return undefined;
    }

    return {
      action: updateAction,
      ids: getItemId(item),
    };
  };

  const renderAction = (handleClose: () => void) => {
    if (groupId === UNGROUPED_RESOURCES_GROUP_ID) {
      return (
        <>
          <AddResourcesToGroup
            resources={[item]}
            onClose={handleClose}
            component="MenuItem"
          />
          {additionalOptions?.(handleClose)}
          {additionalDangerOptions && (
            <>
              <Divider />
              {additionalDangerOptions(handleClose)}
            </>
          )}
        </>
      );
    }

    return (
      <>
        <MoveResources resources={[item]} />
        {additionalOptions?.(handleClose)}
        <Divider />
        <RemoveResourcesFromGroup resources={[item]} onClose={handleClose} />
        {additionalDangerOptions?.(handleClose)}
      </>
    );
  };

  if (!hasActions) {
    return null;
  }

  return (
    <PopoverController
      trigger={
        <IconButton variant="secondary">
          <MoreHorizIcon />
        </IconButton>
      }
      permission={getPermission()}
    >
      {renderAction}
    </PopoverController>
  );
};
